import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, ChevronDown } from "lucide-react";
import api, { COMPANY } from "@/lib/api";
import { Section, PrimaryButton, OutlineButton, GlassCard, Field, inputCls, fadeUp, Toast } from "@/components/ui-kit";

const benefits = [
  { t: "Reliable, professionally maintained vehicles", d: "Late-model WagonRs serviced on a fixed cadence." },
  { t: "Transparent rental terms", d: "Flat weekly rentals. No hidden fees, no surge cuts." },
  { t: "Dedicated driver support", d: "Direct WhatsApp line to ops — not a call-center." },
  { t: "Multi-platform freedom", d: "Drive on Uber, Ola, Rapido — your choice, your hours." },
  { t: "Insurance and paperwork handled", d: "We carry the regulatory load so you can drive." },
  { t: "Top-performer growth track", d: "Priority on new inventory and longer-term programs." },
];

const requirements = [
  "Valid commercial / driving licence",
  "Minimum 1 year of commercial driving experience preferred",
  "Smartphone with at least one mobility platform onboarded",
  "Aadhaar + PAN + address proof",
  "Clean driving record",
];

const faqs = [
  { q: "Is this a self-drive rental?", a: "No. GK Fleet rents vehicles only to professional drivers who use them on Uber, Ola, or Rapido." },
  { q: "What does the weekly rental cover?", a: "Vehicle access, scheduled service, insurance, and operational support." },
  { q: "Can I drive on multiple platforms?", a: "Yes. You decide which platforms you want to use." },
  { q: "Where are you operating today?", a: "Hyderabad. We're expanding deliberately." },
];

export default function DriverProgram() {
  const [form, setForm] = useState({
    name: "", phone: "", city: "Hyderabad", experience_years: "",
    licence_number: "", preferred_platform: "Uber", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/driver-applications", form);
      setSuccess(true);
    } catch (err) {
      setToast("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
      setTimeout(() => setToast(""), 3500);
    }
  };

  return (
    <div data-testid="driver-program-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_0%,rgba(59,130,246,0.15),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-[#3B82F6]">Driver Program</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="mt-5 font-display text-5xl md:text-7xl tracking-tighter font-semibold leading-[1.02]">
            Drive a vehicle built for your business.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-lg text-[#D1D5DB]">
            Join the GK Fleet partner network. Reliable cars, transparent rentals, and a team that has your back.
          </motion.p>
        </div>
      </section>

      <Section eyebrow="Benefits" title="What you get as a partner.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map((b, i) => (
            <motion.div key={b.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}>
              <GlassCard className="h-full">
                <CheckCircle2 className="w-5 h-5 text-[#3B82F6]" />
                <h3 className="mt-5 font-display text-xl">{b.t}</h3>
                <p className="mt-2 text-[#9CA3AF]">{b.d}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Requirements" title="What we look for.">
        <div className="grid md:grid-cols-2 gap-3 max-w-3xl">
          {requirements.map((r) => (
            <div key={r} className="flex items-start gap-3 text-[#D1D5DB]">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#3B82F6]" /> {r}
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Application process" title="Four steps to your first trip.">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Submit application", d: "Takes ~3 minutes." },
            { n: "02", t: "Document verification", d: "Within 24-48 hours." },
            { n: "03", t: "Onboarding briefing", d: "In-person at our Hyderabad HQ." },
            { n: "04", t: "Vehicle handover", d: "Sign rental & drive away." },
          ].map((s, i) => (
            <motion.div key={s.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}>
              <GlassCard className="h-full">
                <p className="font-mono text-xs text-[#9CA3AF]">{s.n}</p>
                <h3 className="mt-3 font-display text-xl">{s.t}</h3>
                <p className="mt-2 text-[#9CA3AF] text-sm">{s.d}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Application Form */}
      <Section id="apply" eyebrow="Apply now" title="Start your application." kicker="We respond to every application within 48 hours.">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            {success ? (
              <GlassCard data-testid="application-success" className="text-center py-16">
                <CheckCircle2 className="w-14 h-14 text-[#3B82F6] mx-auto" />
                <h3 className="mt-6 font-display text-3xl">Application received.</h3>
                <p className="mt-3 text-[#D1D5DB] max-w-md mx-auto">Thanks {form.name?.split(" ")[0] || "there"}. Our team will reach out on {form.phone || "your phone"} within 48 hours.</p>
                <div className="mt-8">
                  <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer">
                    <PrimaryButton><MessageCircle className="w-4 h-4" /> Speak to us on WhatsApp now</PrimaryButton>
                  </a>
                </div>
              </GlassCard>
            ) : (
              <form onSubmit={onSubmit} data-testid="driver-application-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full name" testId="field-name">
                  <input data-testid="input-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
                </Field>
                <Field label="Phone" testId="field-phone">
                  <input data-testid="input-phone" required type="tel" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 ..." />
                </Field>
                <Field label="City" testId="field-city">
                  <input data-testid="input-city" className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </Field>
                <Field label="Driving experience" testId="field-experience">
                  <select data-testid="input-experience" className={inputCls} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })}>
                    <option value="">Select…</option>
                    <option>Less than 1 year</option>
                    <option>1-3 years</option>
                    <option>3-5 years</option>
                    <option>5+ years</option>
                  </select>
                </Field>
                <Field label="Driving licence number" testId="field-licence">
                  <input data-testid="input-licence" className={inputCls} value={form.licence_number} onChange={(e) => setForm({ ...form, licence_number: e.target.value })} placeholder="DL-XXXXXXXXXXXXX" />
                </Field>
                <Field label="Preferred platform" testId="field-platform">
                  <select data-testid="input-platform" className={inputCls} value={form.preferred_platform} onChange={(e) => setForm({ ...form, preferred_platform: e.target.value })}>
                    <option>Uber</option><option>Ola</option><option>Rapido</option><option>Multiple</option>
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Anything we should know?" testId="field-message">
                    <textarea data-testid="input-message" rows={4} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Optional" />
                  </Field>
                </div>
                <div className="md:col-span-2 flex gap-3 items-center pt-2">
                  <PrimaryButton data-testid="submit-application" disabled={loading} type="submit">
                    {loading ? "Submitting…" : "Submit application"}
                  </PrimaryButton>
                  <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer">
                    <OutlineButton type="button"><MessageCircle className="w-4 h-4" /> WhatsApp instead</OutlineButton>
                  </a>
                </div>
              </form>
            )}
          </div>

          <div className="lg:col-span-5">
            <GlassCard>
              <p className="text-xs tracking-[0.2em] uppercase text-[#9CA3AF]">FAQ</p>
              <div className="mt-5 divide-y divide-white/10">
                {faqs.map((f, i) => (
                  <button key={f.q} type="button" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full text-left py-4 group" data-testid={`faq-${i}`}>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-white">{f.q}</span>
                      <ChevronDown className={`w-4 h-4 text-[#9CA3AF] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                    </div>
                    {openFaq === i && <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{f.a}</p>}
                  </button>
                ))}
              </div>
            </GlassCard>
          </div>
        </div>
      </Section>

      <Toast msg={toast} />
    </div>
  );
}
